import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { ChangesResponse } from '@elutide/shared';
import { api, ApiProblem } from '@/lib/api';
import { useDocumentVisible } from '@/lib/hooks';
import {
  CHANGES_POLL_MS,
  applyChangesResponse,
  recordChangesFailure,
  recordChangesSuccess,
} from './changes';

/**
 * Mounts once, inside the shell. Polls `GET /changes?since=` every 10 seconds while the tab
 * is visible and translates the result into query invalidations.
 *
 * It deliberately does not use useQuery: the cursor is a moving value that must not be
 * cached or refetched out of order, and a missed tick must not replay old events.
 */
export function ChangesPoller() {
  const client = useQueryClient();
  const visible = useDocumentVisible();
  const cursor = useRef<number | null>(null);
  const inFlight = useRef(false);

  useEffect(() => {
    if (!visible) return;
    let cancelled = false;

    const tick = async () => {
      if (inFlight.current) return;
      inFlight.current = true;
      try {
        const response = await api.get<ChangesResponse>('/changes', {
          query: cursor.current === null ? {} : { since: cursor.current },
        });
        if (cancelled) return;
        cursor.current = applyChangesResponse(client, response);
        recordChangesSuccess(response.facts_version);
      } catch (error) {
        // A poll failure is not a screen failure. The auth adapter has already been told
        // about a 401 or a lock; anything else is retried on the next tick.
        recordChangesFailure();
        if (!(error instanceof ApiProblem)) throw error;
      } finally {
        inFlight.current = false;
      }
    };

    void tick();
    const id = window.setInterval(() => void tick(), CHANGES_POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [client, visible]);

  return null;
}
